import { useState } from "react";
import TabPanel from "@/components/ui/tab-panel";
import KeyTakeaway from "@/components/ui/key-takeaway";

export default function CurrentLandscape() {
  const [activeTab, setActiveTab] = useState("scientific-advancements");
  const [activeMarketTab, setActiveMarketTab] = useState("biohacking");

  return (
    <section id="landscape" className="mb-16 scroll-mt-20">
      <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#2C3E50] mb-8 pb-2 border-b-2 border-[#ECF0F1]">
        The Current Landscape of Peptide Therapeutics
      </h2>
      
      <div className="flex flex-col md:flex-row gap-8 mb-12">
        <div className="md:w-2/3">
          <h3 className="font-serif text-2xl font-bold text-[#2C3E50] mb-4">Mainstream Medicine</h3>
          <p className="mb-4">
            Peptide drugs have solidly entered mainstream pharma. In the past decade, 
            the FDA has approved dozens of peptide-based medications for conditions like 
            diabetes, osteoporosis, endocrine disorders, and cancer.
          </p>
          
          <p className="mb-4">
            Worldwide sales of peptide drugs were about $20 billion in 2017 and are projected 
            to exceed $50 billion by 2024—highlighting rapid growth. These include household-name 
            medications (e.g., insulin analogs, GLP-1s) and many others that physicians prescribe routinely.
          </p>
          
          <p className="mb-6">
            In short, peptides are now an established pillar of drug development, not just experimental therapy.
          </p>
          
          <div className="bg-[#ECF0F1] p-5 rounded-lg mb-6">
            <h4 className="font-bold text-[#2C3E50] mb-2">Key Market Trends</h4>
            <ul className="list-disc pl-5 space-y-2">
              <li>Rapid growth in research and investment</li>
              <li>Expanding applications beyond traditional endocrine disorders</li>
              <li>Innovation in delivery methods to overcome challenges</li>
              <li>Broader physician adoption across specialties</li>
            </ul>
          </div>
          
          <KeyTakeaway>
            While peptides have been in medicine for a century, we're entering a renaissance period 
            with unprecedented growth. Today's peptide market offers not just established treatments 
            but also cutting-edge experimental therapies targeting previously "undruggable" conditions.
          </KeyTakeaway>
        </div>
        
        <div className="md:w-1/3">
          <div className="bg-white rounded-lg shadow-lg overflow-hidden mb-6">
            <div className="p-5">
              <h4 className="font-bold text-[#2C3E50] mb-3">Peptide Market Growth</h4>
              <div className="bg-[#EBF5FB] p-4 rounded-lg flex items-center justify-center mb-3">
                <svg viewBox="0 0 300 200" className="max-h-48 w-full">
                  {/* Market growth chart */}
                  <rect x="40" y="40" width="240" height="120" fill="none" stroke="#d1d1d1" />
                  <line x1="40" y1="160" x2="280" y2="160" stroke="#333" />
                  <line x1="40" y1="40" x2="40" y2="160" stroke="#333" />
                  
                  {/* X-axis labels */}
                  <text x="70" y="175" fontSize="10" textAnchor="middle">2017</text>
                  <text x="130" y="175" fontSize="10" textAnchor="middle">2020</text>
                  <text x="190" y="175" fontSize="10" textAnchor="middle">2022</text>
                  <text x="250" y="175" fontSize="10" textAnchor="middle">2024</text>
                  
                  {/* Y-axis labels */}
                  <text x="30" y="160" fontSize="10" textAnchor="end">$0</text>
                  <text x="30" y="120" fontSize="10" textAnchor="end">$20B</text>
                  <text x="30" y="80" fontSize="10" textAnchor="end">$35B</text>
                  <text x="30" y="40" fontSize="10" textAnchor="end">$50B</text>
                  
                  {/* Data points and line */}
                  <polyline
                    points="70,140 130,120 190,80 250,40"
                    stroke="#3498DB"
                    strokeWidth="3"
                    fill="none"
                  />
                  
                  <circle cx="70" cy="140" r="4" fill="#3498DB" />
                  <circle cx="130" cy="120" r="4" fill="#3498DB" />
                  <circle cx="190" cy="80" r="4" fill="#3498DB" />
                  <circle cx="250" cy="40" r="4" fill="#3498DB" />
                </svg>
              </div>
              <p className="text-sm text-[#7F8C8D] text-center">
                Projected growth of peptide therapeutics market, 2017-2024
              </p>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow-lg overflow-hidden">
            <div className="p-5">
              <h4 className="font-bold text-[#2C3E50] mb-3">FDA Approvals</h4>
              <p className="text-4xl text-center font-bold text-[#E74C3C] mb-2">100+</p>
              <p className="text-center text-[#7F8C8D]">
                FDA-approved peptide drugs since insulin's introduction
              </p>
            </div>
          </div>
        </div>
      </div>
      
      {/* The Two Worlds of Peptides */}
      <div className="bg-white rounded-lg shadow-lg overflow-hidden mb-10">
        <div className="bg-[#2C3E50] text-white p-4">
          <h3 className="font-serif text-xl font-semibold">The Two Worlds of Peptides</h3>
        </div>
        <div className="p-6">
          <p className="mb-6">
            Parallel to the pharmaceutical industry, there's a growing "underground" or "gray market" peptide scene 
            fueled by biohackers, anti-aging clinics, and functional medicine practitioners. This has created two 
            distinct ecosystems for peptide therapies.
          </p>
          
          {/* Tabs */}
          <div className="border-b border-gray-200 mb-6">
            <ul className="flex flex-wrap -mb-px">
              <li className="mr-2">
                <button 
                  className={`inline-block py-2 px-4 border-b-2 rounded-t-lg hover:text-[#3498DB] ${
                    activeMarketTab === "biohacking" 
                      ? "border-[#3498DB] text-[#3498DB]" 
                      : "border-transparent"
                  }`}
                  onClick={() => setActiveMarketTab("biohacking")}
                >
                  Biohacking & Longevity
                </button>
              </li>
              <li className="mr-2">
                <button 
                  className={`inline-block py-2 px-4 border-b-2 rounded-t-lg hover:text-[#3498DB] ${
                    activeMarketTab === "distribution" 
                      ? "border-[#3498DB] text-[#3498DB]" 
                      : "border-transparent"
                  }`}
                  onClick={() => setActiveMarketTab("distribution")}
                >
                  Distribution Channels
                </button>
              </li>
              <li className="mr-2">
                <button 
                  className={`inline-block py-2 px-4 border-b-2 rounded-t-lg hover:text-[#3498DB] ${
                    activeMarketTab === "fda-actions" 
                      ? "border-[#3498DB] text-[#3498DB]" 
                      : "border-transparent"
                  }`}
                  onClick={() => setActiveMarketTab("fda-actions")}
                >
                  FDA Actions
                </button>
              </li>
            </ul>
          </div>
          
          {/* Tab Content */}
          <TabPanel id="biohacking" active={activeMarketTab === "biohacking"}>
            <div className="flex flex-col md:flex-row gap-6">
              <div className="md:w-2/3">
                <p className="mb-4">
                  A thriving community of biohackers and longevity enthusiasts are using peptides 
                  that are not officially FDA-approved for various purposes:
                </p>
                <ul className="list-disc pl-5 space-y-2 mb-4">
                  <li><strong>BPC-157</strong> - For healing injuries and addressing digestive issues</li>
                  <li><strong>Thymosin Alpha-1</strong> - For immune system enhancement</li>
                  <li><strong>CJC-1295/Ipamorelin</strong> - For growth hormone stimulation and anti-aging effects</li>
                  <li><strong>Epitalon</strong> - For potential longevity effects and telomere lengthening</li>
                </ul>
                <p className="mb-4">
                  This was once a niche phenomenon confined to bodybuilding forums and anti-aging conferences, 
                  but it's rapidly gaining mainstream attention. Today, popular health podcasts, longevity bloggers, 
                  and wellness influencers openly discuss these experimental peptides.
                </p>
                <p>
                  The recent success of GLP-1 peptides for weight loss has sparked broader interest, with more 
                  people wondering what other beneficial peptides might exist for different health concerns.
                </p>
              </div>
              <div className="md:w-1/3">
                <div className="bg-white border border-gray-200 rounded-lg p-4">
                  <h4 className="font-bold text-[#2C3E50] mb-2">Popular "Gray Market" Peptides</h4>
                  <div className="space-y-3">
                    <div className="border-b pb-2">
                      <p className="font-semibold">BPC-157</p>
                      <p className="text-sm text-gray-600">Healing, tissue repair</p>
                    </div>
                    <div className="border-b pb-2">
                      <p className="font-semibold">TB-500</p>
                      <p className="text-sm text-gray-600">Injury recovery, inflammation</p>
                    </div>
                    <div className="border-b pb-2">
                      <p className="font-semibold">Thymosin Alpha-1</p>
                      <p className="text-sm text-gray-600">Immune function</p>
                    </div>
                    <div className="border-b pb-2">
                      <p className="font-semibold">CJC-1295/Ipamorelin</p>
                      <p className="text-sm text-gray-600">Growth hormone release</p>
                    </div>
                    <div>
                      <p className="font-semibold">Epitalon</p>
                      <p className="text-sm text-gray-600">Longevity, telomere extension</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <KeyTakeaway>
              The "biohacking" peptide market operates largely outside traditional medical channels, 
              with enthusiasts experimenting based on early research, anecdotal reports, and theoretical benefits. 
              This creates a tension between innovation and safety concerns.
            </KeyTakeaway>
          </TabPanel>
          
          <TabPanel id="distribution" active={activeMarketTab === "distribution"}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div className="bg-white border border-gray-200 rounded-lg p-5">
                <h4 className="font-bold text-[#2C3E50] mb-3">Research Chemical Vendors</h4>
                <p className="mb-3">
                  A booming cottage industry of online companies sells peptides directly to consumers 
                  under labels like "for research use only." For example, websites like Peptide Sciences 
                  or Limitless Life Nootropics offer vials of peptides without a prescription.
                </p>
                <p className="mb-3">
                  Customers reconstitute these powders and self-inject or take them nasally/orally. 
                  These sales operate in a gray zone legally – the vendors advertise that the products 
                  are not for human use (to avoid FDA regulation), but buyers are often individuals 
                  using them on themselves.
                </p>
                <div className="bg-yellow-50 border-l-4 border-yellow-400 p-3">
                  <p className="text-sm text-yellow-800">
                    <strong>Buyer Beware:</strong> Quality control is inconsistent, and there's a risk of 
                    impurities or mislabeling, as these products aren't regulated like pharmaceuticals.
                  </p>
                </div>
              </div>
              
              <div className="bg-white border border-gray-200 rounded-lg p-5">
                <h4 className="font-bold text-[#2C3E50] mb-3">Compounding Pharmacies</h4>
                <p className="mb-3">
                  On the legitimate side, licensed compounding pharmacies have been a primary source 
                  for physicians who prescribe peptides. A compounding pharmacy can legally create 
                  custom peptide preparations for a patient with a prescription.
                </p>
                <p className="mb-3">
                  For example, a doctor might prescribe thymosin alpha-1 for a cancer patient, 
                  which a compounding pharmacy formulates. This gave clinicians a way to use 
                  peptides that weren't mass-manufactured by big pharma.
                </p>
                <p>
                  Compounded peptides were especially common in wellness and longevity clinics – 
                  until the recent FDA regulatory changes dramatically restricted which peptides 
                  could be legally compounded.
                </p>
              </div>
            </div>
            
            <div className="bg-[#ECF0F1] p-5 rounded-lg mb-4">
              <h4 className="font-bold text-[#2C3E50] mb-2">How Peptides Reach Patients</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
                <div>
                  <div className="rounded-full bg-blue-100 w-16 h-16 flex items-center justify-center mx-auto mb-2">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                  </div>
                  <h5 className="font-semibold mb-1">Pharmaceutical Companies</h5>
                  <p className="text-sm text-gray-600">FDA-approved peptide drugs through traditional medical channels</p>
                </div>
                <div>
                  <div className="rounded-full bg-green-100 w-16 h-16 flex items-center justify-center mx-auto mb-2">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                    </svg>
                  </div>
                  <h5 className="font-semibold mb-1">Compounding Pharmacies</h5>
                  <p className="text-sm text-gray-600">Custom-made peptides with prescription (increasingly restricted)</p>
                </div>
                <div>
                  <div className="rounded-full bg-purple-100 w-16 h-16 flex items-center justify-center mx-auto mb-2">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                    </svg>
                  </div>
                  <h5 className="font-semibold mb-1">Online "Research" Vendors</h5>
                  <p className="text-sm text-gray-600">Direct-to-consumer with "not for human use" disclaimers</p>
                </div>
              </div>
            </div>
            
            <p className="text-sm text-gray-500 italic">
              The distribution landscape has shifted dramatically with recent FDA actions, pushing many peptides 
              further into the gray market and limiting legitimate access channels.
            </p>
          </TabPanel>
          
          <TabPanel id="fda-actions" active={activeMarketTab === "fda-actions"}>
            <div className="mb-6">
              <h4 className="font-bold text-[#2C3E50] mb-3">Recent FDA Regulatory Actions</h4>
              
              <div className="mb-4 border-l-4 border-blue-500 pl-4">
                <h5 className="font-semibold mb-1">2019-2020: Peptide vs. Protein Reclassification</h5>
                <p className="mb-2">
                  In 2019, the FDA refined the definition of peptides vs. proteins. They ruled that any amino acid 
                  polymer longer than 40 amino acids is considered a "protein" (thus a biologic drug), whereas 
                  40 or fewer amino acids is a "peptide."
                </p>
                <p className="text-sm text-gray-600">
                  This matters because under U.S. law, biologics cannot be compounded in regular pharmacies. 
                  By March 2020, this meant that certain longer peptides (like Tesamorelin, a 44-aa peptide) 
                  suddenly became classified as biologics, so compounding pharmacies had to stop making them.
                </p>
              </div>
              
              <div className="mb-6 border-l-4 border-red-500 pl-4">
                <h5 className="font-semibold mb-1">November 2023: Compounded Peptides Ban</h5>
                <p className="mb-2">
                  In November 2023, the FDA took further action specifically targeting commonly compounded peptides. 
                  They banned compounding pharmacies from making or selling 17 popular peptides, including:
                </p>
                <ul className="list-disc pl-5 space-y-1 mb-2">
                  <li>BPC-157</li>
                  <li>Thymosin Alpha-1</li>
                  <li>CJC-1295</li>
                  <li>Ipamorelin</li>
                  <li>Selank</li>
                  <li>Semax</li>
                  <li>And others</li>
                </ul>
                <p className="text-sm text-gray-600">
                  The FDA's reasoning was that there is insufficient safety and efficacy data in humans, 
                  concerns about product quality, and some reports of adverse effects for certain peptides.
                </p>
              </div>
              
              <div className="bg-gray-100 rounded-lg p-4">
                <h5 className="font-semibold mb-2">Impact on Access</h5>
                <p className="mb-3">
                  These regulatory moves have pushed more of the peptide supply chain into the shadows:
                </p>
                <ul className="list-disc pl-5 space-y-2">
                  <li>
                    Doctors who were prescribing (and supervising use of) peptides lost legal access to many of them
                  </li>
                  <li>
                    Patients interested in those peptides now often turn to the "research chemical" market
                  </li>
                  <li>
                    Compounding pharmacies were initially allowed to dispense remaining inventory, but moving 
                    forward they can't compound those banned substances
                  </li>
                </ul>
              </div>
            </div>
            
            <KeyTakeaway>
              The regulatory environment for peptides is in flux – peptides are in a gray area where demand is high, 
              but official approval (and thus legitimate supply) is lagging behind. This has created a situation 
              where innovation and access are balanced against safety and oversight concerns.
            </KeyTakeaway>
          </TabPanel>
        </div>
      </div>
      
      {/* Political Support and Future Outlook */}
      <div className="bg-white rounded-lg shadow-lg overflow-hidden mb-10">
        <div className="bg-[#2C3E50] text-white p-4">
          <h3 className="font-serif text-xl font-semibold">Political Support and Future Outlook</h3>
        </div>
        <div className="p-6">
          <div className="flex flex-col md:flex-row gap-6 mb-6">
            <div className="md:w-2/3">
              <h4 className="font-bold text-[#2C3E50] mb-3">Growing Political Attention</h4>
              <p className="mb-4">
                Peptides have caught the attention of policymakers and public figures who advocate 
                for medical freedom and innovation. Notably, Robert F. Kennedy Jr. (who has been vocal 
                on health topics) has criticized the FDA for restricting access to peptides.
              </p>
              <p className="mb-4">
                In one public statement, RFK Jr. decried <em>"the FDA's aggressive suppression of … peptides, 
                stem cells, [and] hyperbaric therapies"</em> that could benefit health but aren't patentable. 
                This support from political figures suggests there may be growing pressure to create legal 
                pathways for peptide treatments.
              </p>
              <p>
                If such political will translates to policy changes, we could see moves similar to 
                "Right to Try" laws, or pressure on the FDA to approve certain peptides faster or 
                create special access programs.
              </p>
            </div>
            
            <div className="md:w-1/3">
              <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                <div className="bg-[#EBF5FB] p-3">
                  <h5 className="font-semibold text-center">RFK Jr. on Peptides</h5>
                </div>
                <div className="p-4">
                  <div className="bg-gray-100 p-3 rounded-lg mb-3">
                    <svg viewBox="0 0 24 24" className="h-6 w-6 text-blue-400 float-left mr-2">
                      <path fill="currentColor" d="M22.46 6c-.77.35-1.6.58-2.46.69.88-.53 1.56-1.37 1.88-2.38-.83.5-1.75.85-2.72 1.05C18.37 4.5 17.26 4 16 4c-2.35 0-4.27 1.92-4.27 4.29 0 .34.04.67.11.98C8.28 9.09 5.11 7.38 3 4.79c-.37.63-.58 1.37-.58 2.15 0 1.49.75 2.81 1.91 3.56-.71 0-1.37-.2-1.95-.5v.03c0 2.08 1.48 3.82 3.44 4.21a4.22 4.22 0 0 1-1.93.07 4.28 4.28 0 0 0 4 2.98 8.521 8.521 0 0 1-5.33 1.84c-.34 0-.68-.02-1.02-.06C3.44 20.29 5.7 21 8.12 21 16 21 20.33 14.46 20.33 8.79c0-.19 0-.37-.01-.56.84-.6 1.56-1.36 2.14-2.23z"/>
                    </svg>
                    <p className="text-sm italic">
                      "When elected, I will end the FDA's aggressive suppression of off-patent medicines 
                      including peptides, stem cells, hyperbaric therapies and other remedies that can't 
                      be patented by Pharma."
                    </p>
                  </div>
                  <p className="text-xs text-gray-500 text-center">
                    Political interest in peptide access is growing as public awareness increases
                  </p>
                </div>
              </div>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <h4 className="font-bold text-[#2C3E50] mb-3">Regulatory Grey Zone Continues</h4>
              <p className="mb-3">
                In the near term, peptides will remain in a gray area. The FDA is aware of their potential 
                (indeed, continues to approve some as drugs) but is also cautious due to safety unknowns.
              </p>
              <p>
                We're likely to see ongoing debates and possibly new guidance from regulators. For example, 
                the FDA might establish special programs or trials for high-demand peptides (so that people 
                can use them under study protocols rather than on their own).
              </p>
            </div>
            
            <div>
              <h4 className="font-bold text-[#2C3E50] mb-3">Possible Future Scenarios</h4>
              <ul className="space-y-2">
                <li className="flex items-start">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-green-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span><strong>Evidence-Based Approval:</strong> Some currently "experimental" peptides gain enough clinical evidence to become approved therapies</span>
                </li>
                <li className="flex items-start">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-yellow-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  <span><strong>Continued Gray Market:</strong> Underground usage persists through research chemical vendors while official channels remain restricted</span>
                </li>
                <li className="flex items-start">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-blue-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span><strong>Policy Reform:</strong> Political pressure leads to new frameworks allowing broader access under certain conditions</span>
                </li>
              </ul>
            </div>
          </div>
          
          <KeyTakeaway>
            We're witnessing a dynamic tension between growing public interest in peptides, scientific innovation, 
            regulatory caution, and political advocacy. The next few years will likely bring significant developments 
            in how peptides are regulated, accessed, and integrated into mainstream medicine.
          </KeyTakeaway>
        </div>
      </div>
      
      {/* Scientific & Regulatory Landscape */}
      <div className="bg-white rounded-lg shadow-lg overflow-hidden mb-10">
        <div className="bg-[#2C3E50] text-white p-4">
          <h3 className="font-serif text-xl font-semibold">Scientific & Regulatory Landscape</h3>
        </div>
        <div className="p-6">
          {/* Tabs */}
          <div className="border-b border-gray-200 mb-6">
            <ul className="flex flex-wrap -mb-px">
              <li className="mr-2">
                <button 
                  className={`inline-block py-2 px-4 border-b-2 rounded-t-lg hover:text-[#3498DB] ${
                    activeTab === "scientific-advancements" 
                      ? "border-[#3498DB] text-[#3498DB]" 
                      : "border-transparent"
                  }`}
                  onClick={() => setActiveTab("scientific-advancements")}
                >
                  Scientific Advancements
                </button>
              </li>
              <li className="mr-2">
                <button 
                  className={`inline-block py-2 px-4 border-b-2 rounded-t-lg hover:text-[#3498DB] ${
                    activeTab === "delivery-challenges" 
                      ? "border-[#3498DB] text-[#3498DB]" 
                      : "border-transparent"
                  }`}
                  onClick={() => setActiveTab("delivery-challenges")}
                >
                  Delivery Challenges
                </button>
              </li>
              <li className="mr-2">
                <button 
                  className={`inline-block py-2 px-4 border-b-2 rounded-t-lg hover:text-[#3498DB] ${
                    activeTab === "regulatory-framework" 
                      ? "border-[#3498DB] text-[#3498DB]" 
                      : "border-transparent"
                  }`}
                  onClick={() => setActiveTab("regulatory-framework")}
                >
                  Regulatory Framework
                </button>
              </li>
            </ul>
          </div>
          
          {/* Tab Content */}
          <TabPanel id="scientific-advancements" active={activeTab === "scientific-advancements"}>
            <p className="mb-4">Recent advances in peptide science have accelerated discovery and development:</p>
            <ul className="list-disc pl-5 space-y-2 mb-4">
              <li><strong>Computational design</strong> - AI and machine learning to predict peptide properties</li>
              <li><strong>Cyclization techniques</strong> - Creating more stable, longer-lasting peptides</li>
              <li><strong>Peptidomimetics</strong> - Non-natural amino acids and modifications to enhance properties</li>
              <li><strong>High-throughput screening</strong> - Faster identification of promising candidates</li>
            </ul>
            <p>
              These innovations are expanding the therapeutic potential of peptides beyond their traditional applications.
            </p>
          </TabPanel>
          
          <TabPanel id="delivery-challenges" active={activeTab === "delivery-challenges"}>
            <p className="mb-4">Peptides face several delivery challenges that researchers are actively addressing:</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div className="bg-[#ECF0F1] rounded-lg p-4">
                <h4 className="font-bold text-[#2C3E50] mb-2">Challenges</h4>
                <ul className="list-disc pl-5 space-y-1">
                  <li>Poor oral bioavailability (digestive degradation)</li>
                  <li>Short half-life in bloodstream</li>
                  <li>Limited membrane permeability</li>
                  <li>Cold chain requirements for stability</li>
                </ul>
              </div>
              <div className="bg-[#EBF5FB] rounded-lg p-4">
                <h4 className="font-bold text-[#2C3E50] mb-2">Innovative Solutions</h4>
                <ul className="list-disc pl-5 space-y-1">
                  <li>Protease inhibitors and absorption enhancers</li>
                  <li>PEGylation and lipidation to extend half-life</li>
                  <li>Cell-penetrating peptide conjugations</li>
                  <li>Novel formulations and stabilizers</li>
                </ul>
              </div>
            </div>
            <p>These advances are making peptide therapies more convenient and accessible to patients.</p>
          </TabPanel>
          
          <TabPanel id="regulatory-framework" active={activeTab === "regulatory-framework"}>
            <p className="mb-4">The regulatory landscape for peptides is complex and evolving:</p>
            <ul className="list-disc pl-5 space-y-2 mb-4">
              <li><strong>FDA Approval Pathways</strong> - Most therapeutic peptides follow standard drug approval processes</li>
              <li><strong>Compounding Pharmacies</strong> - Can prepare custom peptide formulations under certain circumstances</li>
              <li><strong>Research Peptides</strong> - Sold "for research purposes only," creating regulatory gray areas</li>
              <li><strong>Global Variation</strong> - Significant differences in regulation across countries and regions</li>
            </ul>
            <p className="italic text-[#7F8C8D] mb-4">
              As peptides gain prominence, regulatory frameworks are adapting to balance innovation with safety considerations.
            </p>
            
            <KeyTakeaway>
              The peptide space has a regulatory "wild west" aspect due to the research-use-only market and 
              offshore sourcing. This creates both challenges for safety oversight and opportunities for accelerated 
              innovation outside traditional pharmaceutical pathways.
            </KeyTakeaway>
          </TabPanel>
        </div>
      </div>
      
      <div className="bg-white rounded-lg shadow-lg overflow-hidden">
        <div className="bg-[#2C3E50] text-white p-4">
          <h3 className="font-serif text-xl font-semibold">Challenges and Opportunities</h3>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <h4 className="font-bold text-[#2C3E50] mb-3">Remaining Challenges</h4>
              <ul className="space-y-2">
                <li className="flex items-start">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-[#E74C3C] flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  <span><strong>Delivery Methods:</strong> Most peptides still require injection</span>
                </li>
                <li className="flex items-start">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-[#E74C3C] flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  <span><strong>Manufacturing Costs:</strong> Complex synthesis processes increase prices</span>
                </li>
                <li className="flex items-start">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-[#E74C3C] flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  <span><strong>Stability Issues:</strong> Many require refrigeration and careful handling</span>
                </li>
                <li className="flex items-start">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-[#E74C3C] flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  <span><strong>Regulatory Clarity:</strong> Gray areas in classification and oversight</span>
                </li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-bold text-[#2C3E50] mb-3">Promising Opportunities</h4>
              <ul className="space-y-2">
                <li className="flex items-start">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-[#3498DB] flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span><strong>Expanding Applications:</strong> Beyond traditional areas into neurological, cardiovascular, and immune disorders</span>
                </li>
                <li className="flex items-start">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-[#3498DB] flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span><strong>Novel Delivery Systems:</strong> Oral, transdermal, and inhalable peptide formulations</span>
                </li>
                <li className="flex items-start">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-[#3498DB] flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span><strong>Combination Therapies:</strong> Peptides alongside small molecules or biologics</span>
                </li>
                <li className="flex items-start">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-[#3498DB] flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span><strong>Personalized Medicine:</strong> Tailored peptide therapies based on individual profiles</span>
                </li>
              </ul>
            </div>
          </div>
          
          <p className="italic text-[#7F8C8D]">
            As technology advances and regulatory clarity improves, peptides are poised to transform 
            multiple areas of medicine over the coming decade.
          </p>
        </div>
      </div>
    </section>
  );
}